import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { SessionInfo } from '@cm/protocol';
import {
  effectiveColumns,
  gridPlacement,
  gutterPlacement,
  moveInOrder,
  planMosaic,
  resizeFractions,
  templateWithGutters,
} from '../layout';
import { useActiveLayout, useStore } from '../store';
import { SessionPane } from './SessionPane';

interface Props {
  workspaceId: string;
  sessions: SessionInfo[];
  /** Um pane por vez, em tela cheia. É o modo do celular. */
  single?: boolean;
}

/**
 * Mosaico de panes com divisorias arrastaveis.
 *
 * As divisorias sao itens reais da grade, nao handles posicionados por cima do
 * `gap`: o gap do CSS Grid nao e um elemento e nao recebe evento de mouse.
 * Intercalando faixa/divisoria/faixa o arraste fica trivialmente correto e o
 * layout nao depende de calculo de coordenada absoluta.
 */
export function PaneGrid({ workspaceId, sessions, single = false }: Props) {
  const layout = useActiveLayout();
  const updateLayout = useStore((s) => s.updateLayout);
  const focusedId = useStore((s) => s.focusedId);
  const focus = useStore((s) => s.focus);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragging = useRef<string | null>(null);

  const visible = useMemo(() => {
    const byId = new Map(sessions.map((s) => [s.id, s]));
    return layout.order
      .filter((id) => !layout.minimized.includes(id))
      .map((id) => byId.get(id))
      .filter((s): s is SessionInfo => s !== undefined);
  }, [layout.order, layout.minimized, sessions]);

  // No celular o foco pode ter ficado num pane que esta recolhido ou que ainda
  // nao existe nesta aba. Sem um alvo, a tela ficaria vazia com agentes abertos.
  useEffect(() => {
    if (!single || visible.length === 0) return;
    if (!visible.some((session) => session.id === focusedId)) focus(visible[0].id);
  }, [single, visible, focusedId, focus]);

  const shown = useMemo(() => {
    if (!single) return visible;
    const current = visible.find((session) => session.id === focusedId);
    return current ? [current] : visible.slice(0, 1);
  }, [single, visible, focusedId]);

  const cols = effectiveColumns(layout.columns, single ? shown.length : visible.length);
  const plan = useMemo(
    () => (single ? null : planMosaic(visible.length, cols)),
    [single, visible.length, cols],
  );
  const rows = plan?.rows ?? 1;

  const colFractions = useMemo(
    () => (layout.colFractions.length === cols ? layout.colFractions : Array(cols).fill(1)),
    [layout.colFractions, cols],
  );
  const rowFractions = useMemo(
    () => (layout.rowFractions.length === rows ? layout.rowFractions : Array(rows).fill(1)),
    [layout.rowFractions, rows],
  );

  /**
   * Arraste de divisoria. O delta e calculado como fracao do tamanho total da
   * grade, para o movimento do mouse corresponder ao que se ve na tela
   * independente do zoom ou do tamanho da janela.
   */
  const startResize = useCallback(
    (axis: 'col' | 'row', index: number, event: React.PointerEvent) => {
      event.preventDefault();
      const grid = gridRef.current;
      if (!grid) return;

      const rect = grid.getBoundingClientRect();
      const totalPx = axis === 'col' ? rect.width : rect.height;
      if (totalPx <= 0) return;

      const startPos = axis === 'col' ? event.clientX : event.clientY;
      const base = axis === 'col' ? colFractions : rowFractions;

      const onMove = (move: PointerEvent) => {
        const pos = axis === 'col' ? move.clientX : move.clientY;
        const delta = (pos - startPos) / totalPx;
        const next = resizeFractions(base, index, delta);
        updateLayout(workspaceId, (l) =>
          axis === 'col' ? { ...l, colFractions: next } : { ...l, rowFractions: next },
        );
      };

      const cursorClass = axis === 'col' ? 'resizing-col' : 'resizing-row';
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        document.body.classList.remove(cursorClass);
      };

      // Classe no body para o cursor de redimensionamento valer sobre toda a
      // tela -- sem isso o cursor volta ao normal ao sair de cima da divisoria.
      document.body.classList.add(cursorClass);
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [colFractions, rowFractions, updateLayout, workspaceId],
  );

  const onDropOn = useCallback(
    (targetId: string) => {
      const sourceId = dragging.current;
      dragging.current = null;
      if (!sourceId || sourceId === targetId) return;
      updateLayout(workspaceId, (l) => ({ ...l, order: moveInOrder(l.order, sourceId, targetId) }));
    },
    [updateLayout, workspaceId],
  );

  if (shown.length === 0 || !plan) {
    if (shown.length === 0) return null;
    return (
      <div className="pane-mosaic single" ref={gridRef}>
        {shown.map((session) => (
          <div key={session.id} className="mosaic-cell">
            <SessionPane session={session} focused reorderable={false} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div
      className="pane-mosaic"
      ref={gridRef}
      style={{
        gridTemplateColumns: templateWithGutters(colFractions),
        gridTemplateRows: templateWithGutters(rowFractions),
      }}
    >
      {visible.map((session, index) => {
        const pane = plan.panes[index];
        return (
          <div
            key={session.id}
            className="mosaic-cell"
            style={gridPlacement(pane.col, pane.row, pane.colSpan, pane.rowSpan)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDropOn(session.id)}
          >
            <SessionPane
              session={session}
              focused={session.id === focusedId}
              onDragStart={() => {
                dragging.current = session.id;
              }}
            />
          </div>
        );
      })}

      {/* A divisoria so cobre o trecho em que dois panes se encostam. Num
          arranjo de 3, a horizontal fica so na pilha da esquerda e a vertical
          vai de alto a baixo, ao lado do pane que ocupa o resto. */}
      {plan.gutters.map((gutter) => (
        <div
          key={`${gutter.axis}-${gutter.index}-${gutter.col}-${gutter.row}`}
          className={`gutter gutter-${gutter.axis}`}
          style={gutterPlacement(gutter)}
          onPointerDown={(e) => startResize(gutter.axis, gutter.index, e)}
          title="Arraste para redimensionar"
        />
      ))}
    </div>
  );
}
